import type { FieldConfig } from 'admin/components/pages/Story/CardEditor/Inspector/types';
import { hasValue } from 'admin/components/pages/Story/CardEditor/utils';
import { useAdminSelector } from 'admin/reducers';
import { selectEditableElement } from 'admin/reducers/card-editor-extra/selectors';
import { selectCurrentPlatform, selectCurrentState, selectEditableSymbols } from 'admin/reducers/card-editor/selectors';
import { get } from 'lodash';
import { getInstanceByRef, getMasterByRef, isSymbol } from 'utils/blocks/symbol';

/**
 * Hook to determine if a field's value is defined in the master symbol and/or instance symbol
 *
 * @param field - The field configuration to check
 * @returns Object containing boolean flags for master and instance value existence
 */
interface UseSymbolValueSourceProps {
	field: FieldConfig | undefined;
}

interface SymbolValueSource {
	masterValue: boolean;
	instanceValue: boolean;
}

export const useSymbolValueSource = ({ field }: UseSymbolValueSourceProps): SymbolValueSource => {
	const element = useAdminSelector(selectEditableElement);
	const symbols = useAdminSelector(selectEditableSymbols);
	const currentMediaQuery = useAdminSelector(selectCurrentPlatform);
	const currentState = useAdminSelector(selectCurrentState);

	if (!field || !isSymbol(element)) {
		return { masterValue: false, instanceValue: false };
	}

	const master = getMasterByRef(element.symbol, symbols);
	const instance = getInstanceByRef(element.symbol, symbols);

	// Extract the property path from the field path
	let uiPath = field.path.replace(/^.*?uiConfig\./, 'uiConfig.');
	const currentContextSegment = `${currentState}.${currentMediaQuery}`;

	// If the field has inheritance information, use it to modify the path
	// This is needed because the path may contain current platform/state, but we need to use
	// the platform/state where the property was actually assigned (from inheritance)
	// Otherwise it will only work for uniform properties or when state/platform match unintentionally
	if (field.path.includes(currentContextSegment) && field.inheritance?.state && field.inheritance?.mq) {
		const inheritedContextSegment = `${field.inheritance.state}.${field.inheritance.mq}`;
		uiPath = uiPath.replace(currentContextSegment, inheritedContextSegment);
	}

	const masterValue = master ? get(master, uiPath) : undefined;
	const instanceValue = instance ? get(instance, uiPath) : undefined;

	return {
		masterValue: hasValue(masterValue),
		instanceValue: hasValue(instanceValue),
	};
};
